import { NormalizedNumber } from '@sparkdotfi/common-universal'

import { BackedByPieChartDetailedProps } from '../types'
import { formatPieChartValue } from '../utils'
import { getArcs, getSeparators } from '@/ui/utils/chart-math'
import { cn } from '@/ui/utils/style'

export function BackedByPieChartDetailed({
  displayedItems,
  highlightedKey,
  setHighlightedKey,
  totalAmount,
  ticker,
  pieChartData,
  getHighlightedItem,
  getPieSliceColor,
  onSliceClick,
  className,
}: BackedByPieChartDetailedProps) {
  if (totalAmount.eq(NormalizedNumber.ZERO)) {
    return null
  }

  const { data, unfilteredData } = pieChartData
  const radius = 146
  const strokeWidth = 26
  const arcs = getArcs({ data, cx: 200, cy: 200, radius })
  const separators = getSeparators({ data, unfilteredData, cx: 200, cy: 200, radius, mainStrokeWidth: strokeWidth })
  const highlightedItem = getHighlightedItem()

  return (
    <div className={cn('relative isolate h-fit w-full', className)} style={{ container: 'sidebar / inline-size' }}>
      <svg preserveAspectRatio="xMidYMid meet" viewBox="0 0 400 400">
        {arcs.map((point, index) => {
          if (point.angle <= 0) {
            return null
          }

          const item = displayedItems[index]

          return (
            <path
              d={`M${point.x1} ${point.y1} A${radius} ${radius} 0 ${point.angle > Math.PI ? '1' : '0'} 1 ${point.x2} ${point.y2}`}
              fill="none"
              stroke={getPieSliceColor(index)}
              strokeWidth={strokeWidth}
              style={{
                cursor: 'pointer',
                transition: 'stroke 0.2s ease-in-out',
              }}
              key={`${point.x1}${point.y1}${point.x2}${point.y2}`}
              onMouseEnter={() => {
                if (item) {
                  setHighlightedKey(item.category)
                }
              }}
              onMouseLeave={() => {
                setHighlightedKey(undefined)
              }}
              onClick={() => {
                if (displayedItems[index]) {
                  onSliceClick(displayedItems[index])
                }
              }}
            />
          )
        })}
        {separators.length > 1 &&
          separators.map((separator) => (
            <path
              d={`M${separator.x1} ${separator.y1} L${separator.x2} ${separator.y2}`}
              fill="none"
              stroke="white"
              strokeWidth={2}
              key={`${separator.x1}${separator.y1}${separator.x2}${separator.y2}`}
            />
          ))}
      </svg>
      <div className="-z-10 absolute inset-0">
        <div className="relative flex h-full w-full flex-col items-center justify-center text-primary">
          <div
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center transition-opacity delay-100 duration-200 ease-out',
              highlightedItem === undefined ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <div className="font-roobert text-[3cqw] text-secondary">Total Supply</div>
            <div className="font-roobert text-[7cqw]">{formatPieChartValue(totalAmount)}</div>
            <div className="font-roobert text-[3cqw] text-secondary">{ticker}</div>
          </div>

          {displayedItems.map((item, index) => (
            <div
              key={`highlighted-${index}`}
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center transition-opacity delay-100 duration-200 ease-out',
                highlightedKey === item.category ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              <div className="flex items-center gap-[1cqw]">
                <div className="size-[1.5cqw] rounded-full" style={{ backgroundColor: getPieSliceColor(index) }} />
                <div className="font-roobert text-[3cqw] text-secondary">{item.label}</div>
              </div>
              <div className="font-roobert text-[7cqw]">{formatPieChartValue(item.amount)}</div>
              <div className="font-roobert text-[3cqw] text-secondary">{ticker}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
